/**
 * The Organization chart, built from the persisted `reporting_to` field.
 *
 * There is no hardcoded tree: every edge comes from an employee's saved
 * "Reports To". This module is pure so the shape can be tested without a
 * browser, and it is defensive about the three ways real data goes wrong --
 * a manager who no longer exists, more than one top-level employee, and a
 * reporting cycle -- because a chart that recurses forever or silently drops
 * people is worse than one that admits the data is odd.
 */
import type { EmployeeFull } from "../api/types";

export interface OrgChartNode {
  employee: EmployeeFull;
  /** 0 for a top-level employee, +1 per level of management. */
  depth: number;
  reports: OrgChartNode[];
}

export interface OrgChart {
  /** Top-level nodes, the leading authority first. */
  roots: OrgChartNode[];
  /** Employees whose "Reports To" pointed at a missing or self record. */
  missingManager: string[];
  /** Employees promoted to top level to break a reporting cycle. */
  cycleMembers: string[];
  /** Every employee placed exactly once. */
  size: number;
}

/** Deterministic sibling order: last name, then first name, then code. */
function comparePeople(a: EmployeeFull, b: EmployeeFull): number {
  return (
    (a.last_name || "").localeCompare(b.last_name || "") ||
    (a.first_name || "").localeCompare(b.first_name || "") ||
    (a.employee_code || "").localeCompare(b.employee_code || "") ||
    a.id.localeCompare(b.id)
  );
}

/**
 * Stable: the account owner floats to the top, everyone else keeps their order.
 *
 * Uses the server's `is_account_owner` flag -- the login the account's owner
 * points at -- so no name is hardcoded and no second employee record is
 * invented. An employee list and its chart therefore agree on who leads.
 */
export function ownerFirst<T extends { is_account_owner?: boolean | null }>(people: T[]): T[] {
  return [...people].sort(
    (a, b) => Number(Boolean(b.is_account_owner)) - Number(Boolean(a.is_account_owner)),
  );
}

export function buildOrgChart(people: EmployeeFull[]): OrgChart {
  const byId = new Map(people.map((person) => [person.id, person]));

  // Resolve the manager link only when it points at a live record and is not a
  // self-reference. A missing manager is recorded, never invented.
  const managerOf = new Map<string, string>();
  const missingManager: string[] = [];
  for (const person of people) {
    const managerId = person.reporting_to;
    if (!managerId) continue;
    if (managerId === person.id || !byId.has(managerId)) {
      missingManager.push(person.id);
      continue;
    }
    managerOf.set(person.id, managerId);
  }

  const childrenOf = new Map<string, EmployeeFull[]>();
  for (const person of people) {
    const managerId = managerOf.get(person.id);
    if (!managerId) continue;
    const bucket = childrenOf.get(managerId) ?? [];
    bucket.push(person);
    childrenOf.set(managerId, bucket);
  }
  for (const bucket of childrenOf.values()) bucket.sort(comparePeople);

  // Top level, leading authority first: the account's owner (when they have no
  // manager), then whoever supervises most, then by name. This puts the owner
  // at the top when they are the top-level authority, without naming anyone.
  const topLevel = people
    .filter((person) => !managerOf.has(person.id))
    .sort(
      (a, b) =>
        Number(Boolean(b.is_account_owner)) - Number(Boolean(a.is_account_owner)) ||
        (childrenOf.get(b.id)?.length ?? 0) - (childrenOf.get(a.id)?.length ?? 0) ||
        comparePeople(a, b),
    );

  // DFS with a visited set, so a reporting cycle cannot recurse forever and no
  // one is drawn twice. Any employee left unplaced is that cycle: promote it to
  // a root so the people still appear instead of vanishing.
  const placed = new Set<string>();
  const place = (person: EmployeeFull, depth: number): OrgChartNode => {
    placed.add(person.id);
    const reports = (childrenOf.get(person.id) ?? [])
      .filter((child) => !placed.has(child.id))
      .map((child) => place(child, depth + 1));
    return { employee: person, depth, reports };
  };

  const roots = topLevel.map((person) => place(person, 0));

  // Anyone unreachable from a top-level node is in a cycle. Record them all,
  // then promote each still-unplaced member so the loop is broken and everyone
  // appears exactly once.
  const unplaced = people.filter((person) => !placed.has(person.id));
  const cycleMembers = unplaced.map((person) => person.id);
  for (const person of unplaced) {
    if (placed.has(person.id)) continue;
    roots.push(place(person, 0));
  }

  return { roots, missingManager, cycleMembers, size: placed.size };
}
